import { useRef, useState } from "react";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import {
  BreakdownSettings,
  LabelSelectItem,
  LabelsTurnOffManager,
  useEstimateEditorSettings,
  useEstimateEditorStore,
  useEstimateStructure,
  VisibilityBreakdownSettings,
  VisibilityButton,
  DifferentTotalCostAlert,
  SoftRoundingAlert,
} from "@tools";
import { PopMenu, PhraseSearchBar } from "@components";
import { IconButton, Tooltip, Grid, Badge } from "@material-ui/core";
import { FilterList } from "@material-ui/icons";
import classnames from "classnames";

export const BreakdownTableActions = observer(() => {
  const { t } = useTranslation();
  const { isTemplate, isPresetEditor, allowEdition } = useEstimateEditorSettings();
  const { visibilityMode } = useEstimateEditorStore();
  const structure = useEstimateStructure();
  
  const [ filtersVisible, showFilters ] = useState(false);
  const [ settingsDialogVisible, showSettingsDialog ] = useState(false);
  
  const anchor = useRef(null);
  
  const { searchPhrases, usedLabels, addedLabels, searchLabels, showBreakdownLabelsColumn } = structure;
  const { apply, hideTextSearch, hideLabelSearch, hideLabelsFilter, hideBreakdown } = structure.visibility;
  const { applyDecimalPlaces } = structure.settings;
  
  
  const handleSearchClear = () => {
    structure.removeAllSearchPhrases();
  }
  
  const handleShowFilters = () => {
    if(isPresetEditor)
      return;
    showFilters(!filtersVisible);
  }
  
  const handleSearchPhraseAdd = (newPhrase) => {
    structure.addSearchPhrase(newPhrase);
  }
  
  const handleSearchPhraseRemove = (id) => {
    structure.removeSearchPhrase(id);
  }
  
  const handleAddLabel = (label) => {
    structure.setSearchLabels([...(structure.searchLabels || []), label.systemId]);
  }
  
  const handleRemoveLabel = (label) => {
    structure.setSearchLabels(structure.searchLabels?.filter(l => l !== label.systemId));
  }
  
  const labels = addedLabels?.filter(({ systemId }) => usedLabels.includes(systemId)) || [];
  
  return (
    <Grid
      item container
      alignItems="center"
      wrap="nowrap"
      className="search-bar w-max ml-2"
    >
      {
        visibilityMode &&
        <VisibilityButton
          hidden={hideBreakdown}
          onChange={structure.visibility.setHideBreakdown}
        />
      }
      {
        visibilityMode &&
        <VisibilityBreakdownSettings />
      }
      {
        allowEdition &&
        <BreakdownSettings
          externalVisible={settingsDialogVisible}
          externalOnShow={showSettingsDialog}
        />
      }
      {
        (!apply || !hideTextSearch) &&
        <PhraseSearchBar
          disabled={isPresetEditor}
          searchPhrases={searchPhrases}
          onAdd={handleSearchPhraseAdd}
          onRemove={handleSearchPhraseRemove}
          onClear={handleSearchClear}
          useTooltip={!isPresetEditor}
          tooltipTitle={{
            open: t("views.editor.breakdown_table.search.open"),
            close: t("views.editor.breakdown_table.search.close")
          }}
          placehodler={t("views.editor.breakdown_table.search.placeholder")}
          className="w-max"
          buttonClassName={classnames(
            "text-xl",
            visibilityMode && hideTextSearch && "transparent-2",
            "preset-textSearchButton"
          )}
        >
          {
            visibilityMode &&
            <VisibilityButton
              inRow right={6}
              hidden={hideTextSearch}
              className={classnames(visibilityMode && hideTextSearch && "transparent-2")}
              onChange={structure.visibility.setHideTextSearch}
            />
          }
        </PhraseSearchBar>
      }
      {
        (!apply || !hideLabelSearch) &&
        <>
          {
            showBreakdownLabelsColumn && usedLabels?.length > 0 &&
            <Badge
              color="primary"
              invisible={!searchLabels?.length}
              badgeContent={searchLabels?.length || 0}
              overlap="circular"
              max={9}
              anchorOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
            >
              <Tooltip
                title={t(`views.editor.breakdown_table.label_filter`)}
                disableHoverListener={isPresetEditor}
                disableFocusListener={isPresetEditor}
                disableTouchListener={isPresetEditor}
              >
                <IconButton
                  ref={anchor}
                  onClick={handleShowFilters}
                  className={classnames(
                    "text-xl",
                    visibilityMode && hideLabelSearch && "transparent-2",
                    "preset-labelFilterButton"
                  )}
                  color="primary"
                >
                  <FilterList />
                </IconButton>
              </Tooltip>
            </Badge>
          }
          {
            showBreakdownLabelsColumn && usedLabels?.length > 0 && visibilityMode &&
            <VisibilityButton
              inRow right={6}
              hidden={hideLabelSearch}
              className={classnames(visibilityMode && hideLabelSearch && "transparent-2")}
              onChange={structure.visibility.setHideLabelSearch}
            />
          }
        </>
      }
      {
        (!apply || !hideLabelsFilter) && showBreakdownLabelsColumn && usedLabels?.length > 0 &&
        <LabelsTurnOffManager />
      }
      {
        allowEdition &&
        <DifferentTotalCostAlert />
      }
      {
        allowEdition && applyDecimalPlaces &&
        <SoftRoundingAlert showBreakdownSettingsDialog={showSettingsDialog} />
      }
      
      {
        filtersVisible &&
        <PopMenu
          show={filtersVisible}
          anchor={anchor.current}
          id="labelFilter"
          onClickAway={() => showFilters(false)}
          style={{ maxWidth: 240 }}
          className="py-2"
          showArrow
        >
          <Grid item container className="max-h-list w-full overflow-auto">
            {
              !usedLabels?.length &&
              <p className="text-secondary my-2 mx-4">
                { t(`views.editor.labels.not_found_in_${isTemplate ? "template" : "project"}`) }
              </p>
            }
            {
              labels.map((label) => (
                <LabelSelectItem
                  key={`${label.name}_${label.id}`}
                  label={label}
                  isUsed={Boolean(searchLabels?.includes(label.systemId))}
                  onAdd={handleAddLabel}
                  onRemove={handleRemoveLabel}
                  maxWidth={178}
                >
                  {label.name}
                </LabelSelectItem>
              ))
            }
          </Grid>
        </PopMenu>
      }
    </Grid>
  );
});